import type { ReactNode } from "react";

import { useDocumentMeta } from "@/brand";
import { Card } from "@/components/ui";
import { cn } from "@/lib/cn";
import { SiteFrame } from "@/site/SiteFrame";

/**
 * The public site's header and footer around an account page (sign in, sign
 * up, set password): the form on the left and a short plain panel on the
 * right on desktop; one column on phones, form first.
 */
export function AccountFrame({
  title,
  aside,
  form = "narrow",
  children,
}: {
  title: string;
  aside?: ReactNode;
  /** "narrow" for a few fields (sign in), "wide" for long forms (sign up). */
  form?: "narrow" | "wide";
  children: ReactNode;
}) {
  useDocumentMeta(title);
  return (
    <SiteFrame>
      <div
        className={cn(
          "mx-auto grid w-full gap-6 py-2 sm:py-6 lg:items-start lg:gap-10",
          !aside
            ? "max-w-3xl"
            : form === "narrow"
              ? "max-w-[1040px] lg:grid-cols-[minmax(0,460px)_minmax(0,1fr)]"
              : "max-w-[1120px] lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)]",
        )}
      >
        <div className="min-w-0">{children}</div>
        {aside ? <aside className="grid min-w-0 gap-4 lg:sticky lg:top-24">{aside}</aside> : null}
      </div>
    </SiteFrame>
  );
}

/** The form's card, with the page heading in the site's display face. */
export function AccountCard({
  title,
  intro,
  children,
  className,
}: {
  title: ReactNode;
  intro?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("grid gap-5 p-5 sm:p-8", className)}>
      <div className="grid gap-1">
        <h1 className="font-display text-[1.75rem] leading-tight font-bold text-fg">{title}</h1>
        {intro ? <div className="text-sm text-muted">{intro}</div> : null}
      </div>
      {children}
    </Card>
  );
}

/** A tinted side panel, like the plan tiles on the home page. */
export function AccountPanel({ title, children, className }: { title: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("min-w-0 rounded-2xl border border-border bg-tint-blue/60 p-5 sm:p-6", className)}>
      <h2 className="mb-3 font-display text-xl font-bold text-fg">{title}</h2>
      <div className="grid gap-3 text-sm leading-relaxed text-muted">{children}</div>
    </section>
  );
}

/** Short bullet list for the side panels. */
export function PanelList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="grid gap-2">
      {items.map((item, i) => (
        <li key={i} className="grid grid-cols-[0.75rem_1fr] gap-2">
          <span className="mt-2 size-1.5 rounded-full bg-brand" aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Numbered steps, for "how it works". */
export function PanelSteps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="grid gap-3">
      {items.map((item, i) => (
        <li key={i} className="grid grid-cols-[1.75rem_1fr] items-start gap-3">
          <span className="num grid size-7 place-items-center rounded-full bg-surface text-xs font-semibold text-brand ring-1 ring-border">
            {i + 1}
          </span>
          <span className="pt-0.5">{item}</span>
        </li>
      ))}
    </ol>
  );
}

/** A form-level message (wrong password, server down). */
export function FormAlert({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded-lg bg-down-soft px-3 py-2 text-sm text-down">
      {children}
    </p>
  );
}
